<script setup lang="ts">
/**
 * A calm full-screen message with stacked actions: the companion account screens, "Update Lighthouse",
 * "AI consent needed" and "Account deleted". `overlay` covers the whole app (fixed, above every sheet and toast);
 * otherwise it fills its container. Put buttons in the default slot with the `fsn-btn` classes.
 */
import LighthouseLogo from '@/assets/images/LogoOrange.png'

withDefaults(defineProps<{
    title: string
    body?: string
    icon?: string
    overlay?: boolean
}>(), { body: '', icon: '', overlay: false })
</script>

<template>
    <div
        class="fsn native-app-safe"
        :class="{ 'fsn--overlay': overlay }"
        :role="overlay ? 'dialog' : undefined"
        :aria-modal="overlay ? 'true' : undefined"
        :aria-label="title"
    >
        <div class="fsn-body">
            <img :src="LighthouseLogo" alt="Lighthouse" class="fsn-logo" />
            <span v-if="icon" class="fsn-icon" aria-hidden="true"><i :class="icon" /></span>
            <h1 class="fsn-title">{{ title }}</h1>
            <p v-if="body" class="fsn-text">{{ body }}</p>
            <slot name="extra" />
        </div>
        <div class="fsn-actions">
            <slot />
        </div>
    </div>
</template>

<style scoped>
.fsn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 32px;
    width: 100%;
    height: 100%;
    min-height: 100%;
    padding: max(24px, env(safe-area-inset-top, 0px)) 24px max(24px, env(safe-area-inset-bottom, 0px));
    background: var(--lh-page, #fafafa);
    overflow-y: auto;
}
.fsn--overlay {
    position: fixed;
    inset: 0;
    z-index: 10500;
}
.fsn-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 380px;
}
.fsn-logo {
    height: 36px;
    margin-bottom: 28px;
}
.fsn-icon {
    display: grid;
    place-items: center;
    width: 52px;
    height: 52px;
    margin-bottom: 16px;
    border-radius: 16px;
    font-size: 22px;
    color: var(--lh-accent, #fb4c1f);
    background: rgb(251 76 31 / 0.1);
}
.fsn-title {
    font-size: 20px;
    font-weight: 600;
    color: var(--lh-text-1, #111);
}
.fsn-text {
    margin-top: 8px;
    font-size: 15px;
    line-height: 1.5;
    color: var(--lh-text-3, #6b6b6b);
}
.fsn-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    max-width: 360px;
}
.fsn-actions :slotted(.fsn-btn) {
    min-height: 48px;
    border-radius: 999px;
    font-size: 15px;
    font-weight: 600;
    color: var(--lh-text-1, #111);
    background: var(--lh-surface, #fff);
    box-shadow: 0 0 0 1px rgb(15 15 15 / 0.1);
}
.fsn-actions :slotted(.fsn-btn:disabled) {
    opacity: 0.55;
}
.fsn-actions :slotted(.fsn-btn--primary) {
    color: #fff;
    background: #111;
    box-shadow: none;
}
.fsn-actions :slotted(.fsn-btn--danger) {
    color: #dc2626;
}
html.dark .fsn-actions :slotted(.fsn-btn) {
    box-shadow: 0 0 0 1px rgb(255 255 255 / 0.12);
}
html.dark .fsn-actions :slotted(.fsn-btn--primary) {
    color: #111;
    background: #f5f5f5;
}
</style>
